import React, { useState } from 'react';
import { useRevenueTrends } from '../hooks';

type PeriodType = 'monthly' | 'quarterly' | 'yearly';

const RevenueTrendsChart: React.FC = () => {
  const [period, setPeriod] = useState<PeriodType>('monthly');
  const currentYear = new Date().getFullYear();
  const [selectedYears, setSelectedYears] = useState<number[]>([currentYear, currentYear - 1]);
  
  const { data, isLoading } = useRevenueTrends(period, selectedYears);

  const periodOptions: { value: PeriodType; label: string }[] = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleYearToggle = (year: number) => {
    setSelectedYears((prev) =>
      prev.includes(year)
        ? prev.filter((y) => y !== year)
        : [...prev, year].sort((a, b) => b - a)
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Revenue Trends</h2>
        </div>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  const chartData = data || {};
  const years = Object.keys(chartData).map(Number).sort((a, b) => b - a);
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (years.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Revenue Trends</h2>
        </div>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No revenue data available
        </div>
      </div>
    );
  }

  // Get all periods for the chart
  const allPeriods = new Set<string>();
  years.forEach((year) => {
    const yearData = chartData[year] || [];
    yearData.forEach((item) => allPeriods.add(item.period));
  });
  const sortedPeriods = Array.from(allPeriods).sort();

  const maxRevenue = Math.max(
    ...years.flatMap((year) =>
      (chartData[year] || []).map((item) => item.revenue)
    ),
    0
  );

  const chartHeight = 300;
  const chartWidth = 1000;
  const padding = 60;
  const usableHeight = chartHeight - padding * 2;
  const usableWidth = chartWidth - padding * 2;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Revenue Trends</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Period:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as PeriodType)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {periodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm text-gray-600">Years:</label>
            {yearOptions.map((year) => (
              <label key={year} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedYears.includes(year)}
                  onChange={() => handleYearToggle(year)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">{year}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="relative" style={{ height: `${chartHeight}px` }}>
        <svg
          width="100%"
          height={chartHeight}
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="overflow-visible"
        >
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + usableHeight * (1 - ratio);
            const value = maxRevenue * ratio;
            return (
              <g key={ratio}>
                <line
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                <text
                  x={padding - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="text-xs fill-gray-500"
                >
                  €{value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </text>
              </g>
            );
          })}

          {/* Lines for each year */}
          {years.map((year, yearIndex) => {
            const yearData = chartData[year] || [];
            if (yearData.length === 0) return null;

            const points = sortedPeriods.map((period, index) => {
              const item = yearData.find((d) => d.period === period);
              const x = padding + (index * (usableWidth / (sortedPeriods.length - 1 || 1)));
              const y =
                padding +
                usableHeight -
                ((item?.revenue || 0) / maxRevenue) * usableHeight;
              return { x, y, revenue: item?.revenue || 0, period };
            });

            const pathData = points
              .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
              .join(' ');

            const color = colors[yearIndex % colors.length];

            return (
              <g key={year}>
                <path
                  d={pathData}
                  fill="none"
                  stroke={color}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.8"
                />
                {points.map((point, index) => (
                  <circle
                    key={index}
                    cx={point.x}
                    cy={point.y}
                    r="5"
                    fill={color}
                    className="hover:r-7 transition-all cursor-pointer"
                  >
                    <title>
                      {point.period} ({year}): €
                      {point.revenue.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </title>
                  </circle>
                ))}
              </g>
            );
          })}

          {/* X-axis labels */}
          {sortedPeriods.map((period, index) => {
            const x = padding + (index * (usableWidth / (sortedPeriods.length - 1 || 1)));
            return (
              <text
                key={index}
                x={x}
                y={chartHeight - padding + 20}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {period}
              </text>
            );
          })}

          {/* Legend */}
          <g transform={`translate(${chartWidth - padding - 150}, ${padding})`}>
            {years.map((year, index) => {
              const color = colors[index % colors.length];
              return (
                <g key={year} transform={`translate(0, ${index * 20})`}>
                  <line
                    x1="0"
                    y1="5"
                    x2="20"
                    y2="5"
                    stroke={color}
                    strokeWidth="3"
                  />
                  <text
                    x="25"
                    y="8"
                    className="text-xs fill-gray-700"
                  >
                    {year}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default RevenueTrendsChart;
